import React from "react";
import HeadlineKpiCard from "../../../components/ui-components/HeadlineKpiCard";
import { humanEUR } from "../../cost-control/forecast/format";
import { compactNumber } from "../format";

// Period-based headline KPIs. Deliberately four, and deliberately economic:
// active models and active API keys are metadata, not outcomes, so they are
// not promoted here.
//
// API-equivalent value is the differentiated one. Subscription AI usage
// behaves unlike normal cloud consumption — €10,000 of equivalent API
// consumption can cost €300 in actual subscription fee — and reporting only
// the €300 invites the reading that AI is economically insignificant.
function deltaLine(pct, hasComparison) {
  if (!hasComparison) return { sub: null, subTone: undefined };
  const rounded = Math.round(pct * 100);
  const up = rounded >= 0;
  return { sub: `${up ? "+" : ""}${rounded}% vs prior period`, subTone: up ? "text-danger" : "text-success" };
}

export default function TrendsKpiStrip({ kpis }) {
  const k = kpis || {};
  const has = Boolean(k.hasComparison);
  const avoidedPct = Math.round((k.avoidedPct || 0) * 100);

  const tokens = deltaLine(k.tokensDeltaPct || 0, has);
  const events = deltaLine(k.eventsDeltaPct || 0, has);
  const actual = deltaLine(k.actualDeltaPct || 0, has);

  // Idle capacity is ADDED to the comparison, not substituted for it: the note
  // used to replace the delta, so the actual-cost KPI silently lost its
  // period-over-period change on exactly the tenants where idle capacity
  // exists. Both facts are true and neither is optional.
  const idleNote = k.idleCapacity > 0 ? `${humanEUR(k.idleCapacity)} of it idle capacity` : null;
  const actualSub = (actual.sub || idleNote) ? (
    <>
      {actual.sub}
      {idleNote && <span className="block text-text-3">{idleNote}</span>}
    </>
  ) : null;

  // avoided is deliberately NOT clamped at zero. avoided = apiEquivalent -
  // actual, and actual includes idle capacity (subscription fee on days with
  // no AI usage), so a badly underused subscription can legitimately cost
  // MORE than its API-equivalent value — avoided goes negative. That is a
  // real signal (the subscription cost more than pay-per-use would have),
  // not a bug, so it is reported as an excess rather than as a nonsensical
  // "-€500 avoided". Mirrors AnalyzerKpiStrip's flip to "% used" when an AI
  // subscription is over-utilised (see AnalyzerKpiStrip.jsx).
  const avoided = k.avoided || 0;
  const overApiEquivalent = avoided < 0;
  const avoidedSub = k.apiEquivalent > 0
    ? overApiEquivalent
      ? `${humanEUR(Math.abs(avoided))} more than API-equivalent`
      : `${humanEUR(avoided)} avoided · ${avoidedPct}% avoided`
    : null;
  const avoidedTone = overApiEquivalent ? "text-warning" : "text-success";

  return (
    <div className="flex gap-3">
      <HeadlineKpiCard
        className="flex-1"
        label="AI consumption"
        value={`${compactNumber(k.tokens || 0)} tokens`}
        sub={tokens.sub}
        subTone={tokens.subTone}
      />
      <HeadlineKpiCard
        className="flex-1"
        label="AI events"
        value={`${compactNumber(k.events || 0)} requests`}
        sub={events.sub}
        subTone={events.subTone}
      />
      <HeadlineKpiCard
        className="flex-1"
        label="Actual AI cost"
        value={humanEUR(k.actual || 0)}
        sub={actualSub}
        subTone={actual.subTone}
      />
      <HeadlineKpiCard
        className="flex-1"
        label="API-equivalent value"
        value={humanEUR(k.apiEquivalent || 0)}
        sub={avoidedSub}
        subTone={avoidedTone}
      />
    </div>
  );
}
